import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ESkeletonType } from '@norfolk-southern/accessns-components';
import { Subject } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';

import { NSCommonConstants } from '@ruby/configs/common.constants';
import { IThirdPartyLOA, IThirdPartySearchCompany } from '@ruby/modules/third-party/models/third-party.interface';
import { IError } from '@ruby/shared/models/request/error.interface';

@Component({
  selector: 'ng-select-company-provider',
  templateUrl: './select-company-provider.component.html',
  styleUrls: ['./select-company-provider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectCompanyProviderComponent implements OnDestroy, OnChanges {
  @Input() isLoading!: boolean;
  @Input() isEdit!: boolean;
  @Input() error?: IError;
  @Input() companyProviders!: Array<IThirdPartySearchCompany>;
  @Input() contract?: IThirdPartyLOA;
  @Input() providerCompany?: IThirdPartySearchCompany;
  @Output() search = new EventEmitter<string>();
  @Output() clear = new EventEmitter();
  @Output() selectCompanyProvider = new EventEmitter<IThirdPartySearchCompany>();
  skeleton: ESkeletonType = ESkeletonType.LIST;
  pattern = NSCommonConstants.specialCharCheck;
  provider: UntypedFormGroup = new UntypedFormGroup({
    providerCompany: new UntypedFormControl('', this.validator())
  });
  radioButton: UntypedFormGroup = new UntypedFormGroup({});
  searchProviderName = new Subject<string>();
  providerSelected?: IThirdPartySearchCompany;
  isDisabledRadio = false;
  isSelectedProvider = false;

  constructor() {
    this.searchProviderName
      .pipe(
        filter((value: string) => value.length > 2 || value.length === 0),
        debounceTime(400)
      ).subscribe((value: string) => {
      if (value.trim()) {
        this.providerSelected = undefined;
        this.clear.emit();
        this.emitSearchThirdPartyCompanies(value.trim());
      } else {
        this.clearText();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.contract && changes.contract.currentValue && this.isEdit) {
      this.emitSearchThirdPartyCompanies(changes.contract.currentValue.companyTPLName);
      this.select3PLProvider({
        name: changes.contract.currentValue.companyTPLName,
        eid: changes.contract.currentValue.companyTPLEid
      });
    }
    this.isDisabledRadio = changes.providerCompany && !changes.providerCompany.currentValue;
    this.isSelectedProvider = changes.providerCompany && changes.providerCompany.currentValue;
  }

  ngOnDestroy(): void {
    this.searchProviderName.unsubscribe();
  }

  /**
   * On Select 3PL Provider
   *
   * @summary The following method sets a company provider and enabled the next button
   * @param $company: IThirdPartySearchCompany
   * @returns void
   */
  select3PLProvider($company: IThirdPartySearchCompany): void {
    if (this.providerSelected?.name !== $company.name) {
      this.providerSelected = $company;
      this.selectCompanyProvider.emit(this.providerSelected);
    }
  }

  /**
   * Next
   *
   * @summary The following method emits the selected provider and do next on mat-stepper
   * @returns void
   */
  next(): void {
    this.selectCompanyProvider.emit(this.providerSelected);
  }

  /**
   * Clear Text
   *
   * @summary The following method emits the action to clear the provider company and clean the text field
   * @returns void
   */
  clearText() {
    this.provider.controls.providerCompany.reset('');
    this.providerSelected = undefined;
    this.clear.emit();
  }

  /**
   * Validator
   *
   * @summary Autocomplete Validator
   * @returns ValidatorFn
   */
  validator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      let result = {};
      if (typeof control.value === 'string' && control.value.trim().length < 1) {
        result = { invalid: { value: control.value } };
      }
      return result;
    };
  }

  private emitSearchThirdPartyCompanies(value: string): void {
    this.search.emit(value);
  }
}
